import type { EntityKind, EntityRef } from "@/components/entity-preview/entity-kinds";
import { getNotebookDocument } from "@/gen_src/proto/chalk/notebook/v1/document_service-NotebookDocumentService_connectquery";
import {
  getDataset,
  listDatasetRevisions,
} from "@/gen_src/proto/chalk/server/v1/datasets-DatasetMetadataService_connectquery";
import { listModelVersions } from "@/gen_src/proto/chalk/server/v1/model_registry-ModelRegistryService_connectquery";
import { getWorksheetNode } from "@/gen_src/proto/chalk/server/v1/worksheets-WorksheetsService_connectquery";
import { prettyFormatAgo } from "@/utils/date-time";
import { timestampDate, type Timestamp } from "@bufbuild/protobuf/wkt";
import { useQuery } from "@connectrpc/connect-query";

/** A labelled row on a preview. A null value renders a fixed-width skeleton while it loads, so the
 *  preview keeps its size when the value arrives. */
export interface EntityPreviewField {
  label: string;
  value: string | null;
}

/** What a kind adds to its preview from live data, beyond the registry's static metadata. */
export interface EntityPreviewDetails {
  description?: string;
  fields?: readonly EntityPreviewField[];
}

type EntityPreviewDetailsHook = (entity: EntityRef) => EntityPreviewDetails;

/** A loaded value, an em dash when the fetch failed, or null (skeleton) while it loads. */
const fieldValue = (
  query: { data: unknown; isError: boolean },
  getValue: () => string | undefined,
): string | null => {
  if (query.data != null) return getValue() ?? "—";
  return query.isError ? "—" : null;
};

/** `count`, or `count+` when a paginated listing was truncated. */
const formatCount = (count: number, truncated: boolean): string =>
  `${count.toLocaleString()}${truncated ? "+" : ""}`;

const formatAgo = (timestamp: Timestamp | undefined): string | undefined =>
  timestamp == null ? undefined : prettyFormatAgo(timestampDate(timestamp));

const useDatasetDetails: EntityPreviewDetailsHook = (entity) => {
  const datasetQuery = useQuery(getDataset, { id: entity.id });
  const revisionsQuery = useQuery(listDatasetRevisions, { datasetId: entity.id });
  return {
    fields: [
      {
        label: "Revisions",
        value: fieldValue(revisionsQuery, () => {
          const revisions = revisionsQuery.data?.revisions;
          return revisions == null
            ? undefined
            : formatCount(revisions.length, (revisionsQuery.data?.cursor ?? "") !== "");
        }),
      },
      {
        label: "Columns (latest revision)",
        value: fieldValue(datasetQuery, () =>
          datasetQuery.data?.dataset?.mostRecentRevision?.outputColumns.length.toLocaleString(),
        ),
      },
    ],
  };
};

// The versions listing keys off the model name; one page this large is effectively "all" for a
// preview, with `+` marking the rare truncation.
const MODEL_VERSIONS_PAGE_SIZE = 500;

const useModelDetails: EntityPreviewDetailsHook = (entity) => {
  const versionsQuery = useQuery(listModelVersions, {
    modelName: entity.name,
    limit: MODEL_VERSIONS_PAGE_SIZE,
  });
  return {
    fields: [
      {
        label: "Versions",
        value: fieldValue(versionsQuery, () => {
          const versions = versionsQuery.data?.modelVersions;
          return versions == null
            ? undefined
            : formatCount(versions.length, (versionsQuery.data?.nextCursor ?? "") !== "");
        }),
      },
    ],
  };
};

const useNotebookDetails: EntityPreviewDetailsHook = (entity) => {
  const documentQuery = useQuery(getNotebookDocument, { notebookId: entity.id });
  const document = documentQuery.data?.document;
  return {
    description: document?.description || undefined,
    fields: [
      {
        label: "Cells",
        value: fieldValue(documentQuery, () => document?.cells.length.toLocaleString()),
      },
      {
        label: "Views",
        value: fieldValue(documentQuery, () => document?.totalViewCount.toLocaleString()),
      },
      {
        label: "Last edited",
        value: fieldValue(documentQuery, () => formatAgo(document?.updatedAt)),
      },
    ],
  };
};

const useWorksheetDetails: EntityPreviewDetailsHook = (entity) => {
  const nodeQuery = useQuery(getWorksheetNode, { nodeId: entity.id });
  const node = nodeQuery.data?.node;
  return {
    fields: [
      { label: "Views", value: fieldValue(nodeQuery, () => node?.totalViewCount.toLocaleString()) },
      {
        label: "You last opened",
        value: fieldValue(nodeQuery, () => formatAgo(node?.viewerLastViewedAt) ?? "Never"),
      },
    ],
  };
};

const useNoDetails: EntityPreviewDetailsHook = () => ({});

const ENTITY_PREVIEW_DETAIL_HOOKS: Partial<Record<EntityKind, EntityPreviewDetailsHook>> = {
  dataset: useDatasetDetails,
  model: useModelDetails,
  notebook: useNotebookDetails,
  worksheet: useWorksheetDetails,
};

/**
 * The live-details hook for a kind. Callers must not switch kinds within one mounted component
 * (each kind's hook calls different queries); key the component by kind instead.
 */
export const getEntityPreviewDetailsHook = (kind: EntityKind): EntityPreviewDetailsHook =>
  ENTITY_PREVIEW_DETAIL_HOOKS[kind] ?? useNoDetails;
